import React, {useState} from 'react';
import {Box, Grid, Typography, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from '@mui/material';
import SchoolDashboardShell from '../components/SchoolDashboard.comp';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import {NAVY, ORANGE, TEAL, PageHeader, BandChip, headCell, bodyCell} from '../components/widgets.comp';
import {useDashboard} from '../../../context/DashboardContext';
import {findCard, cellText, rowsFrom} from '../schoolData';
import {rowMatches} from '../../DashboardCommon/dashboardCards';
import {RowCell} from '../../DashboardCommon/SuppressedValue.comp';
import {useTableControls} from '../../DashboardCommon/useTableControls';
import {SortFilterHeaderCell} from '../../DashboardCommon/SortFilterHeaderCell.comp';
import {TableToolbar} from '../../DashboardCommon/TableToolbar.comp';

const BLUE = '#3B6FA0';

/* Slide 39 — Program Deployment Tracker. KPIs and the program list table are
   copied verbatim from the deck's own table/shapes (not PerformanceSummary's
   dummy rows, which don't match this deck). "Show all deployed + created
   programs." is a yellow annotation (build instruction), not on-page copy,
   so it isn't rendered — the table below already shows every row. Accent
   colors are this dashboard's own tokens (TEAL/BLUE/NAVY), not the deck's
   raw sample colors (0E8A6E/2E75B6/6A4C93). */
/* Live data (SD-39-01 … 05, SD-40-02), in this page's own layout:
     KPIs          SD.S_18.C_2 Active Programs · C_3 Classes Assigned · C_4 Program Completion
     Program List  SD.S_18.C_6   ·   Tracker by Class  SD.S_18.C_7
   "Priority Curriculum Coverage" (SD-39-04) is not drawn — the Data Map marks it
   removed from the datahouse output by D-10. Every column of both tables sorts
   and filters from its header (QA 9 Oct 2026 — the Grade/Program/Class drop-down
   bar is gone); each table's search box matches any cell. */
const SIGNAL_ORDER = ['Strong', 'Moderate', 'Early', 'Weak'];
const PROGRAM_COLUMNS = [
    {key: 'program', label: 'Program', type: 'text', align: 'left'},
    {key: 'creator', label: 'Program Creator', type: 'text', align: 'left'},
    {key: 'targetArea', label: 'Target Area', type: 'text', align: 'left'},
    {key: 'classes', label: 'Classes', type: 'number'},
    {key: 'enrolled', label: 'Students Enrolled', type: 'number'},
    {key: 'completed', label: 'Students Completed', type: 'number'},
    {key: 'rate', label: 'Completion Rate', type: 'percent'},
    {key: 'status', label: 'Status', type: 'band'},
];
const BY_CLASS_COLUMNS = [
    {key: 'grade', label: 'Grade', type: 'text', align: 'left'},
    {key: 'cls', label: 'Class', type: 'text'},
    {key: 'teacher', label: 'Teacher', type: 'text'},
    {key: 'program', label: 'Program', type: 'text'},
    {key: 'targetArea', label: 'Target COS Area', type: 'text'},
    {key: 'start', label: 'Start Date', type: 'date'},
    {key: 'enrolled', label: 'Students Enrolled', type: 'number'},
    {key: 'completed', label: 'Students Completed', type: 'number'},
    {key: 'rate', label: 'Completion Rate', type: 'percent'},
    {key: 'pre', label: 'Avg. Pre-Assessment', type: 'number'},
    {key: 'post', label: 'Avg. Post-Assessment', type: 'number'},
    {key: 'impact', label: 'Avg. Impact Rate', type: 'percent'},
    {key: 'signal', label: 'Impact Signal', type: 'band', order: SIGNAL_ORDER},
];

const kpiText = (card, pct) => {
    if (!card) return '—';
    const v = card.kpi_value;
    if (pct && v !== null && v !== '' && Number.isFinite(Number(v))) return `${Math.trunc(Number(v))}%`;
    return cellText(v);
};

const emptyRow = (cols, text) => (
    <TableRow>
        <TableCell colSpan={cols} sx={{...bodyCell, textAlign: 'center', py: 4, color: '#6A7282'}}>{text}</TableCell>
    </TableRow>
);
const headerCells = (t, columns) => columns.map((c, i) => (
    <SortFilterHeaderCell key={c.key} {...t.headerProps(c.key)} sx={{...headCell, textAlign: c.align || 'center', pl: i === 0 ? 2.5 : 2}}/>
));
const zebra = (i) => ({backgroundColor: i % 2 === 0 ? 'var(--brand-surface, #ffffff)' : 'var(--brand-surface-subtle, #F9FAFB)'});
const NO_MATCH = 'No row matches the search and filters.';

const StatCardAccent = ({label, value, sub, accent}) => (
    <Box sx={{
        display: 'flex', border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '10px',
        backgroundColor: 'var(--brand-surface, #ffffff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        overflow: 'hidden', height: '100%',
    }}>
        <Box sx={{width: '5px', flexShrink: 0, backgroundColor: accent}}/>
        <Box sx={{p: 2, flex: 1}}>
            <Typography sx={{
                fontSize: '10.5px', fontWeight: 700, color: '#6A7282', letterSpacing: '0.6px',
                textTransform: 'uppercase', mb: 1,
            }}>
                {label}
            </Typography>
            <Typography sx={{fontSize: '26px', fontWeight: 800, color: NAVY, lineHeight: 1}}>
                {value}
            </Typography>
            {sub && <Typography sx={{fontSize: '11px', color: '#9CA3AF', mt: 0.75}}>{sub}</Typography>}
        </Box>
    </Box>
);

const DeploymentStats = ({stats}) => (
    <Grid container spacing={2.5} sx={{mb: 4}}>
        {stats.map((s) => (
            <Grid item xs={12} sm={6} md={4} key={s.label}>
                <StatCardAccent {...s}/>
            </Grid>
        ))}
    </Grid>
);

const rateColor = (rate) => {
    const num = parseFloat(rate);
    if (isNaN(num)) return '#374151';
    return num >= 75 ? TEAL : '#E8730C';
};

const ProgramsTable = ({rows, emptyText, query, onQuery}) => {
    const t = useTableControls(rows, PROGRAM_COLUMNS);
    return (
        <>
            <TableToolbar {...t} query={query} onQuery={onQuery} placeholder="Search programs…"/>
            <TableContainer sx={{
                border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            }}>
                <Table>
                    <TableHead>
                        <TableRow>{headerCells(t, PROGRAM_COLUMNS)}</TableRow>
                    </TableHead>
                    <TableBody>
                        {t.rows.length === 0 && emptyRow(8, emptyText)}
                        {t.rows.map((row, i) => (
                            <TableRow key={`${row.program}-${rows.indexOf(row)}`} sx={zebra(i)}>
                                <TableCell sx={{...bodyCell, textAlign: 'left', fontWeight: 700, color: NAVY, pl: 2.5}}>{row.program}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'left'}}>{row.creator}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'left'}}>{row.targetArea}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.classes}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.enrolled}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.completed}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center', fontWeight: 700, color: rateColor(row.rate)}}>{row.rate}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.status}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

const impactSignalColor = (signal) => {
    if (signal === 'Strong') return TEAL;
    if (signal === 'Moderate') return '#EFA020';
    return '#6A7282';
};

const ByClassTable = ({rows, emptyText, query, onQuery}) => {
    const t = useTableControls(rows, BY_CLASS_COLUMNS);
    return (
        <>
            <TableToolbar {...t} query={query} onQuery={onQuery} placeholder="Search by grade, class, program…"/>
            <TableContainer sx={{
                border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', overflow: 'auto',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            }}>
                <Table sx={{minWidth: 1100}}>
                    <TableHead>
                        <TableRow>{headerCells(t, BY_CLASS_COLUMNS)}</TableRow>
                    </TableHead>
                    <TableBody>
                        {t.rows.length === 0 && emptyRow(BY_CLASS_COLUMNS.length, emptyText)}
                        {t.rows.map((row, i) => (
                            <TableRow key={`${row.cls}-${row.program}-${rows.indexOf(row)}`} sx={zebra(i)}>
                                <TableCell sx={{...bodyCell, textAlign: 'left', fontWeight: 700, color: NAVY, pl: 2.5}}>{row.grade}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center', fontWeight: 700, color: NAVY}}>{row.cls}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.teacher}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.program}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.targetArea}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.start}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.enrolled}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.completed}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.rate}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}><RowCell row={row} k="pre"/></TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}><RowCell row={row} k="post"/></TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center', fontWeight: 700}}><RowCell row={row} k="impact"/></TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row._suppressed?.signal ? <RowCell row={row} k="signal"/> : (row.signal === '—' ? '—' : <BandChip label={row.signal} color={impactSignalColor(row.signal)}/>)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

const PROGRAM_DEPLOYMENT_SECTION_CODE = 'S_18';
const CARD_SECTIONS = [PROGRAM_DEPLOYMENT_SECTION_CODE];

const ProgramDeploymentPage = () => {
    const [activeTab, setActiveTab] = useState(0);
    const [programQuery, setProgramQuery] = useState('');
    const [classQuery, setClassQuery] = useState('');
    const {dashboardData, loading} = useDashboard({ card_sections: CARD_SECTIONS });
    const card = (c) => findCard(dashboardData, PROGRAM_DEPLOYMENT_SECTION_CODE, c);

    const stats = [
        {label: 'Active Programs', value: kpiText(card('C_2')), sub: 'deployed / created', accent: TEAL},
        {label: 'Classes Assigned', value: kpiText(card('C_3')), accent: BLUE},
        {label: 'Program Completion', value: kpiText(card('C_4'), true), accent: BLUE},
    ];

    const programRows = rowsFrom(card('C_6'), {
        program: 'Program', creator: 'Program Creator', targetArea: 'Target Area', classes: 'Classes',
        enrolled: 'Students Enrolled', completed: 'Students Completed', rate: 'Completion Rate %', status: 'Status',
    });
    const classRows = rowsFrom(card('C_7'), {
        grade: 'Grade', cls: 'Class', teacher: 'Teacher', program: 'Program', targetArea: 'Target Area',
        start: 'Start Date', enrolled: 'Students Enrolled', completed: 'Students Completed', rate: 'Completion Rate %',
        pre: 'Avg. Pre', post: 'Avg. Post', impact: 'Avg. Impact Rate %', signal: 'Impact Signal',
    });
    const pq = programQuery.trim().toLowerCase();
    const cq = classQuery.trim().toLowerCase();
    const shownPrograms = programRows.filter((r) => rowMatches(r, pq));
    const shownClasses = classRows.filter((r) => rowMatches(r, cq));

    if (loading) {
        return (
            <SchoolDashboardShell>
                <CircularLoader />
            </SchoolDashboardShell>
        );
    }

    if (!dashboardData) {
        return (
            <SchoolDashboardShell>
                <NoDataAvailable />
            </SchoolDashboardShell>
        );
    }

    return (
        <SchoolDashboardShell
            tabs={
                <Tabs
                    value={activeTab}
                    onChange={(_, v) => setActiveTab(v)}
                    TabIndicatorProps={{style: {backgroundColor: ORANGE}}}
                    sx={{
                        borderBottom: '1px solid #E5E7EB',
                        mb: 2,
                        '& .MuiTab-root': {textTransform: 'none', fontWeight: 600, fontSize: '14px', color: '#6A7282', px: 2},
                        '& .Mui-selected': {color: `${ORANGE} !important`},
                    }}
                >
                    <Tab label="Program List"/>
                    <Tab label="Tracker by Class"/>
                </Tabs>
            }
        >
            <PageHeader
                title="Program Deployment Tracker"
                subtitle="Are recommended programs actually being created, assigned, completed, and assessed?"
            />

            {activeTab === 0 ? (
                <Box>
                    <Typography sx={{fontSize: '17px', fontWeight: 700, color: NAVY, mb: 1.5}}>
                        Key Performance Indicators
                    </Typography>
                    <DeploymentStats stats={stats}/>

                    <Typography sx={{fontSize: '17px', fontWeight: 700, color: NAVY, mb: 1.5}}>
                        List of Total Programs and Deployment Status
                    </Typography>
                    <ProgramsTable
                        rows={shownPrograms}
                        query={programQuery}
                        onQuery={setProgramQuery}
                        emptyText={programRows.length ? NO_MATCH : 'No programs created or deployed yet.'}
                    />
                </Box>
            ) : (
                <Box>
                    <ByClassTable
                        rows={shownClasses}
                        query={classQuery}
                        onQuery={setClassQuery}
                        emptyText={classRows.length ? NO_MATCH : 'No programs deployed to classes yet.'}
                    />
                </Box>
            )}
        </SchoolDashboardShell>
    );
};

export default ProgramDeploymentPage;
